import { useEffect, useId, useRef, useState } from "react";
import type { ClipboardEvent, KeyboardEvent, ReactNode } from "react";
import {
  Check,
  ChevronRight,
  LoaderCircle,
  MapPin,
  Search,
} from "lucide-react";
import { api } from "../api";
import type { AddressComponents } from "../address-model";
import "./address-autocomplete.css";

export interface AddressFieldIssue {
  field: keyof AddressComponents;
  reason: string;
}
export interface AddressCapabilities {
  enabled: boolean;
  provider: string | null;
  countries: string[];
  message?: string;
  maxQueryLength?: number;
  autocompleteQueryLimit?: number;
  freeformQueryLimit?: number;
}
export interface AddressSuggestion {
  id: string;
  label: string;
  components: AddressComponents;
  entries: number;
  selectionToken?: string;
}
export interface AddressVerification {
  status: "VERIFIED" | "CORRECTED" | "UNVERIFIED";
  components: AddressComponents;
  fieldIssues: AddressFieldIssue[];
  provider: string;
  verifiedAt?: string;
}
/** Injectable only for isolated UI tests. Production always uses the authenticated server adapter. */
export interface AddressService {
  capabilities(signal: AbortSignal): Promise<AddressCapabilities>;
  suggest(
    request: { query: string; country: string; selected?: string },
    signal: AbortSignal,
  ): Promise<{ suggestions: AddressSuggestion[]; provider: string }>;
  resolve(
    request: { query: string; country: string },
    signal: AbortSignal,
  ): Promise<{ suggestions: AddressSuggestion[]; provider: string }>;
  verify(
    request: { components: AddressComponents },
    signal: AbortSignal,
  ): Promise<AddressVerification>;
}
export interface AddressAutocompleteProps {
  value: AddressComponents;
  onChange: (next: AddressComponents) => void;
  sectionLabel: string;
  label: string;
  disabled?: boolean;
  id?: string;
  children?: ReactNode;
  onFieldIssues?: (issues: AddressFieldIssue[]) => void;
  fieldTargets?: Partial<Record<keyof AddressComponents, string>>;
  service?: AddressService;
}

const addressFields = [
  "street",
  "unit",
  "city",
  "state",
  "postalCode",
  "country",
] as const;
const fieldLabels: Record<keyof AddressComponents, string> = {
  street: "Street address",
  unit: "Apartment, suite, or unit",
  city: "City",
  state: "State",
  postalCode: "ZIP code",
  country: "Country",
};
const serverService: AddressService = {
  capabilities: (signal) => api("/addresses/capabilities", { signal }),
  suggest: (request, signal) =>
    api("/addresses/suggest", {
      method: "POST",
      body: JSON.stringify(request),
      signal,
    }),
  resolve: (request, signal) =>
    api("/addresses/resolve", {
      method: "POST",
      body: JSON.stringify(request),
      signal,
    }),
  verify: (request, signal) =>
    api("/addresses/verify", {
      method: "POST",
      body: JSON.stringify(request),
      signal,
    }),
};
export function addressValueKey(value: AddressComponents): string {
  return JSON.stringify(addressFields.map((field) => value[field]));
}
function isAddress(value: unknown): value is AddressComponents {
  return (
    !!value &&
    typeof value === "object" &&
    addressFields.every(
      (field) => typeof (value as AddressComponents)[field] === "string",
    )
  );
}
export function canVerifyAddress(value: AddressComponents): boolean {
  return (
    isAddress(value) &&
    value.country === "US" &&
    [value.street, value.city, value.state, value.postalCode].every(
      (part) => part.trim().length > 0,
    )
  );
}
export function displayAddress(value: AddressComponents): string {
  return [
    value.street,
    value.unit,
    [value.city, value.state, value.postalCode].filter(Boolean).join(" "),
    value.country,
  ]
    .filter(Boolean)
    .join(", ");
}
/** Syntax and request-size checks only; never infer city/state/ZIP consistency without Smarty. */
export function addressInputIssues(
  value: AddressComponents,
): AddressFieldIssue[] {
  if (value.country !== "US") return [];
  const issues: AddressFieldIssue[] = [];
  const limits: Record<keyof AddressComponents, number> = {
    street: 50,
    unit: 32,
    city: 64,
    state: 2,
    postalCode: 10,
    country: 2,
  };
  const states = new Set(
    "AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY".split(
      " ",
    ),
  );
  for (const field of addressFields) {
    if (/[\x00-\x1f\x7f]/.test(value[field]))
      issues.push({
        field,
        reason: `${fieldLabels[field]} cannot include line breaks or control characters. Enter each address part in its own field.`,
      });
    else if (
      field === "state" &&
      value.state.trim() &&
      (value.state.length > 2 || !states.has(value.state.trim().toUpperCase()))
    )
      issues.push({
        field,
        reason:
          "Use the two-letter state abbreviation for one of the 50 states or District of Columbia, such as CA or DC.",
      });
    else if (
      field === "postalCode" &&
      value.postalCode.trim() &&
      !/^\d{5}(?:-\d{4})?$/.test(value.postalCode.trim())
    )
      issues.push({
        field,
        reason: "Use five digits or ZIP+4, such as 12345 or 12345-6789.",
      });
    else if (value[field].length > limits[field])
      issues.push({
        field,
        reason: `${fieldLabels[field]} must contain no more than ${limits[field]} characters for address verification. Keep apartment or suite details in their separate field.`,
      });
  }
  return issues;
}
function actionableError(error: unknown): string | undefined {
  if (!error || typeof error !== "object") return undefined;
  const known = error as { code?: unknown; message?: unknown };
  return typeof known.code === "string" &&
    [
      "ADDRESS_INPUT_INVALID",
      "ADDRESS_SELECTION_EXPIRED",
      "ADDRESS_RATE_LIMITED",
    ].includes(known.code) &&
    typeof known.message === "string" &&
    known.message.length <= 500
    ? known.message
    : undefined;
}
function validSuggestion(value: AddressSuggestion): boolean {
  return (
    !!value &&
    typeof value.id === "string" &&
    !!value.id &&
    typeof value.label === "string" &&
    !!value.label &&
    isAddress(value.components) &&
    value.components.country === "US" &&
    Number.isSafeInteger(value.entries) &&
    value.entries >= 0 &&
    (value.selectionToken === undefined ||
      typeof value.selectionToken === "string")
  );
}
function validVerification(value: AddressVerification): boolean {
  return (
    !!value &&
    ["VERIFIED", "CORRECTED", "UNVERIFIED"].includes(value.status) &&
    isAddress(value.components) &&
    typeof value.provider === "string" &&
    !!value.provider &&
    Array.isArray(value.fieldIssues) &&
    value.fieldIssues.every(
      (issue) =>
        !!issue &&
        addressFields.includes(issue.field) &&
        typeof issue.reason === "string" &&
        !!issue.reason.trim(),
    )
  );
}
type SearchState = {
  scope: string;
  status:
    "idle" | "loading" | "ready" | "empty" | "error" | "unit" | "too-long";
  suggestions: AddressSuggestion[];
  message?: string;
};
type VerificationState = {
  scope: string;
  valueKey: string;
  epoch: number;
  status: "loading" | "error" | "done";
  result?: AddressVerification;
  message?: string;
};

/** Manual-selection WAI combobox: focus stays in the input; Tab/blur never commits a suggestion. */
export function AddressAutocomplete({
  value,
  onChange,
  sectionLabel,
  label,
  disabled = false,
  id,
  children,
  onFieldIssues,
  fieldTargets,
  service = serverService,
}: AddressAutocompleteProps) {
  const generatedId = useId();
  const scope = id || generatedId;
  const inputId = `${scope}-address-search`,
    listId = `${scope}-address-suggestions`,
    noticeId = `${scope}-address-notice`;
  const valueKey = addressValueKey(value);
  const [capability, setCapability] = useState<{
    scope: string;
    status: "loading" | "ready" | "error";
    value?: AddressCapabilities;
  }>({ scope, status: "loading" });
  const [query, setQuery] = useState({
    scope,
    text: "",
    enabled: false,
    selected: undefined as string | undefined,
  });
  const [search, setSearch] = useState<SearchState>({
    scope,
    status: "idle",
    suggestions: [],
  });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [epoch, setEpoch] = useState(0);
  const [verifyAgain, setVerifyAgain] = useState(0);
  const [verification, setVerification] = useState<VerificationState | null>(
    null,
  );
  const [keptCorrection, setKeptCorrection] = useState("");
  const capabilityGeneration = useRef(0),
    searchGeneration = useRef(0),
    verifyGeneration = useRef(0);
  const searchAbort = useRef<AbortController | null>(null),
    verifyAbort = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const focused = useRef(false);
  const popupAllowed = useRef(false);
  const issuesCallback = useRef(onFieldIssues);
  issuesCallback.current = onFieldIssues;
  // Checked during callbacks as well as effect cleanup, including a parent change before cleanup runs.
  const live = useRef({ scope, valueKey, disabled, epoch });
  live.current = { scope, valueKey, disabled, epoch };
  const caps =
    capability.scope === scope && capability.status === "ready"
      ? capability.value
      : undefined;
  const foreign = value.country !== "" && value.country !== "US";
  const enabled =
    !disabled && !foreign && !!caps?.enabled && caps.countries.includes("US");
  const currentQuery =
    query.scope === scope
      ? query
      : { scope, text: "", enabled: false, selected: undefined };
  const currentSearch =
    search.scope === scope
      ? search
      : ({ scope, status: "idle", suggestions: [] } as SearchState);
  const currentVerification =
    enabled &&
    verification?.scope === scope &&
    verification.valueKey === valueKey &&
    verification.epoch === epoch
      ? verification
      : null;
  const result =
    currentVerification?.status === "done"
      ? currentVerification.result
      : undefined;
  const syntaxIssues = enabled ? addressInputIssues(value) : [];
  const displayedIssues = syntaxIssues.length
    ? syntaxIssues
    : result?.fieldIssues || [];
  const verified =
    result?.status === "VERIFIED" &&
    result.fieldIssues.length === 0 &&
    addressValueKey(result.components) === valueKey &&
    canVerifyAddress(value);
  const correction =
    result?.status === "CORRECTED" &&
    result.components.country === "US" &&
    addressValueKey(result.components) !== valueKey
      ? result.components
      : undefined;
  const suggestions = currentSearch.suggestions;
  const listVisible = enabled && open && suggestions.length > 0;

  useEffect(() => {
    const generation = ++capabilityGeneration.current,
      controller = new AbortController();
    setCapability({ scope, status: "loading" });
    if (!disabled)
      service
        .capabilities(controller.signal)
        .then((response) => {
          if (
            controller.signal.aborted ||
            capabilityGeneration.current !== generation ||
            live.current.scope !== scope ||
            live.current.disabled
          )
            return;
          if (
            !response ||
            typeof response.enabled !== "boolean" ||
            !Array.isArray(response.countries) ||
            !response.countries.every((country) => typeof country === "string")
          )
            throw new Error("Invalid capability response");
          setCapability({ scope, status: "ready", value: response });
        })
        .catch(() => {
          if (
            !controller.signal.aborted &&
            capabilityGeneration.current === generation &&
            live.current.scope === scope
          )
            setCapability({ scope, status: "error" });
        });
    return () => controller.abort();
  }, [scope, disabled, service]);

  useEffect(() => {
    setQuery({ scope, text: "", enabled: false, selected: undefined });
    setOpen(false);
    setActive(-1);
    setSearch({ scope, status: "idle", suggestions: [] });
    setVerification(null);
    setKeptCorrection("");
    issuesCallback.current?.([]);
  }, [scope]);

  useEffect(() => {
    const generation = ++searchGeneration.current,
      controller = new AbortController();
    searchAbort.current = controller;
    setActive(-1);
    if (
      !enabled ||
      !currentQuery.enabled ||
      currentQuery.text.trim().length < 3
    ) {
      setSearch((previous) =>
        previous.scope === scope && previous.status === "unit" && enabled
          ? previous
          : { scope, status: "idle", suggestions: [] },
      );
      setOpen(false);
      return () => controller.abort();
    }
    if (!currentQuery.selected && currentQuery.text.length > 100) {
      setSearch({ scope, status: "too-long", suggestions: [] });
      setOpen(false);
      return () => controller.abort();
    }
    setSearch({ scope, status: "loading", suggestions: [] });
    const request = {
      query: currentQuery.text,
      country: "US",
      ...(currentQuery.selected ? { selected: currentQuery.selected } : {}),
    };
    const timer = setTimeout(() => {
      const responsePromise =
        !request.selected && request.query.length > 32
          ? service.resolve(
              { query: request.query, country: "US" },
              controller.signal,
            )
          : service.suggest(request, controller.signal);
      responsePromise
        .then((response) => {
          if (
            controller.signal.aborted ||
            searchGeneration.current !== generation ||
            live.current.scope !== scope ||
            live.current.valueKey !== valueKey ||
            live.current.disabled
          )
            return;
          if (
            !response ||
            typeof response.provider !== "string" ||
            !Array.isArray(response.suggestions) ||
            !response.suggestions.every(validSuggestion) ||
            new Set(response.suggestions.map((item) => item.id)).size !==
              response.suggestions.length
          )
            throw new Error("Invalid suggestions");
          setSearch({
            scope,
            status: response.suggestions.length ? "ready" : "empty",
            suggestions: response.suggestions,
          });
          setOpen(
            focused.current &&
              popupAllowed.current &&
              response.suggestions.length > 0,
          );
        })
        .catch((error) => {
          if (
            !controller.signal.aborted &&
            searchGeneration.current === generation &&
            live.current.scope === scope &&
            live.current.valueKey === valueKey
          )
            setSearch({
              scope,
              status: "error",
              suggestions: [],
              message: actionableError(error),
            });
        });
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [
    enabled,
    scope,
    currentQuery.text,
    currentQuery.enabled,
    currentQuery.selected,
    valueKey,
    service,
  ]);

  useEffect(() => {
    const generation = ++verifyGeneration.current,
      controller = new AbortController();
    verifyAbort.current = controller;
    setVerification(null);
    setKeptCorrection("");
    issuesCallback.current?.([]);
    const localIssues = enabled ? addressInputIssues(value) : [];
    if (localIssues.length) {
      issuesCallback.current?.(localIssues);
      return () => controller.abort();
    }
    if (!enabled || !canVerifyAddress(value)) return () => controller.abort();
    const snapshot = { ...value },
      requestEpoch = live.current.epoch;
    const timer = setTimeout(() => {
      if (live.current.epoch !== requestEpoch) return;
      setVerification({
        scope,
        valueKey,
        epoch: requestEpoch,
        status: "loading",
      });
      service
        .verify({ components: snapshot }, controller.signal)
        .then((response) => {
          if (
            controller.signal.aborted ||
            verifyGeneration.current !== generation ||
            live.current.scope !== scope ||
            live.current.valueKey !== valueKey ||
            live.current.epoch !== requestEpoch ||
            live.current.disabled
          )
            return;
          if (!validVerification(response))
            throw new Error("Invalid verification response");
          // Changed returned strings are a proposed correction, not evidence for the submitted strings.
          const checked =
            response.status === "VERIFIED" &&
            addressValueKey(response.components) !== valueKey
              ? { ...response, status: "CORRECTED" as const }
              : response;
          setVerification({
            scope,
            valueKey,
            epoch: requestEpoch,
            status: "done",
            result: checked,
          });
          issuesCallback.current?.(checked.fieldIssues);
        })
        .catch((error) => {
          if (
            !controller.signal.aborted &&
            verifyGeneration.current === generation &&
            live.current.scope === scope &&
            live.current.valueKey === valueKey &&
            live.current.epoch === requestEpoch
          ) {
            setVerification({
              scope,
              valueKey,
              epoch: requestEpoch,
              status: "error",
              message: actionableError(error),
            });
            issuesCallback.current?.([]);
          }
        });
    }, 500);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // Epoch deliberately does not restart a check when only uncommitted search text changes.
  }, [enabled, scope, valueKey, verifyAgain, service]);

  useEffect(() => {
    if (listVisible && active >= 0)
      document
        .getElementById(`${listId}-${active}`)
        ?.scrollIntoView({ block: "nearest" });
  }, [listVisible, active, listId]);

  function invalidateVerification() {
    verifyAbort.current?.abort();
    ++verifyGeneration.current;
    const next = live.current.epoch + 1;
    live.current.epoch = next;
    setEpoch(next);
    setVerification(null);
    setKeptCorrection("");
    issuesCallback.current?.(enabled ? addressInputIssues(value) : []);
  }
  function editSearch(text: string) {
    if (disabled) return;
    searchAbort.current?.abort();
    ++searchGeneration.current;
    invalidateVerification();
    popupAllowed.current = true;
    setQuery({ scope, text, enabled: true, selected: undefined });
    setSearch({ scope, status: "idle", suggestions: [] });
    setActive(-1);
    setOpen(false);
  }
  function pasteSearch(event: ClipboardEvent<HTMLInputElement>) {
    if (!enabled) return;
    const pasted = event.clipboardData.getData("text/plain");
    if (!pasted) return;
    event.preventDefault();
    const start =
        event.currentTarget.selectionStart ?? currentQuery.text.length,
      end = event.currentTarget.selectionEnd ?? start;
    // Preserve separators for the server's explicit full-address parser. The browser's default
    // single-line paste would discard line breaks and concatenate street and city names.
    editSearch(
      currentQuery.text.slice(0, start) + pasted + currentQuery.text.slice(end),
    );
  }
  function selectSuggestion(suggestion: AddressSuggestion) {
    if (!enabled) return;
    invalidateVerification();
    if (suggestion.entries > 1) {
      if (suggestion.selectionToken) {
        popupAllowed.current = true;
        setQuery({
          scope,
          text: suggestion.label,
          enabled: true,
          selected: suggestion.selectionToken,
        });
        setSearch({ scope, status: "loading", suggestions: [] });
        setActive(-1);
      } else {
        setSearch({ scope, status: "unit", suggestions: [] });
        setQuery({
          scope,
          text: currentQuery.text,
          enabled: false,
          selected: undefined,
        });
        setOpen(false);
      }
      return;
    }
    if (!canVerifyAddress(suggestion.components)) {
      setSearch({ scope, status: "unit", suggestions: [] });
      setQuery({
        scope,
        text: currentQuery.text,
        enabled: false,
        selected: undefined,
      });
      setOpen(false);
      return;
    }
    setQuery({
      scope,
      text: suggestion.label,
      enabled: false,
      selected: undefined,
    });
    setOpen(false);
    setActive(-1);
    setSearch({ scope, status: "idle", suggestions: [] });
    onChange({ ...suggestion.components });
    setVerifyAgain((count) => count + 1);
  }
  function keyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.nativeEvent.isComposing) return;
    if (
      (event.key === "ArrowDown" || event.key === "ArrowUp") &&
      suggestions.length &&
      enabled
    ) {
      event.preventDefault();
      popupAllowed.current = true;
      setOpen(true);
      setActive((index) =>
        event.key === "ArrowDown"
          ? Math.min(index + 1, suggestions.length - 1)
          : index < 0
            ? suggestions.length - 1
            : Math.max(index - 1, 0),
      );
    } else if (event.key === "Enter" && listVisible && active >= 0) {
      event.preventDefault();
      selectSuggestion(suggestions[active]);
    } else if (event.key === "Escape") {
      if (open) event.preventDefault();
      popupAllowed.current = false;
      setOpen(false);
      setActive(-1);
    } else if (event.key === "Tab") {
      popupAllowed.current = false;
      setOpen(false);
      setActive(-1);
    }
  }
  function manualEntry() {
    popupAllowed.current = false;
    setOpen(false);
    setActive(-1);
    searchAbort.current?.abort();
    ++searchGeneration.current;
    setQuery({
      scope,
      text: currentQuery.text,
      enabled: false,
      selected: undefined,
    });
    setSearch({ scope, status: "idle", suggestions: [] });
    const target =
      fieldTargets?.street && document.getElementById(fieldTargets.street);
    const manual = document.getElementById(`${scope}-address-manual`);
    (
      target ||
      manual?.querySelector<HTMLElement>(
        "input:not(:disabled),select:not(:disabled),button:not(:disabled)",
      ) ||
      manual
    )?.focus();
  }
  let notice =
    "Type at least 3 characters or paste a full US address. Your fields change only when you select an address.";
  if (disabled)
    notice =
      "This address is read-only. Address search and verification are paused.";
  else if (foreign)
    notice =
      "Enter this international address in the fields below. US address search and verification do not apply.";
  else if (capability.scope !== scope || capability.status === "loading")
    notice =
      "Checking address-search availability. You can enter the address manually below.";
  else if (capability.status === "error")
    notice =
      "Address search is temporarily unavailable. Enter the address manually below.";
  else if (!enabled)
    notice =
      "Address search is not configured. Enter the address manually below; it has not been verified.";
  else if (currentSearch.status === "loading")
    notice = currentQuery.selected
      ? "Loading apartment and suite choices…"
      : "Searching US addresses…";
  else if (currentSearch.status === "empty")
    notice =
      "No matching address found. Try more detail, including a ZIP code, or enter the address manually.";
  else if (currentSearch.status === "error")
    notice = currentSearch.message
      ? `${sectionLabel} — Address search: ${currentSearch.message}`
      : "Address search could not be completed. Try again or enter the address manually.";
  else if (currentSearch.status === "unit")
    notice =
      "This result needs an apartment, suite, or other address detail. Enter the complete address manually below.";
  else if (currentSearch.status === "too-long")
    notice =
      "Address search accepts up to 100 characters. Shorten the search text, or enter the complete address in the fields below. Your text has not been shortened.";
  else if (currentSearch.status === "ready" && suggestions.length)
    notice = `${suggestions.length} address ${suggestions.length === 1 ? "suggestion" : "suggestions"} available. Use the arrow keys, then Enter to select. Tab leaves your address unchanged.`;
  return (
    <section
      className="address-autocomplete"
      aria-label={`${sectionLabel} — ${label}`}
    >
      <div className="address-autocomplete__heading">
        <span className="address-autocomplete__mark">
          <MapPin size={19} aria-hidden="true" />
        </span>
        <div>
          <h4>{label}</h4>
          <p>Search a US address, or enter each field yourself.</p>
        </div>
        {verified && (
          <span className="address-autocomplete__verified">
            <Check size={15} aria-hidden="true" />
            Address verified
          </span>
        )}
      </div>
      <div className="address-autocomplete__search-wrap">
        <label htmlFor={inputId}>Search US addresses</label>
        <div className="address-autocomplete__search">
          <Search size={19} aria-hidden="true" />
          <input
            ref={inputRef}
            id={inputId}
            type="text"
            role="combobox"
            autoComplete="off"
            spellCheck={false}
            value={currentQuery.text}
            disabled={!enabled}
            placeholder="Start typing a street address or paste the full address"
            aria-autocomplete="list"
            aria-expanded={listVisible}
            aria-controls={listId}
            aria-activedescendant={
              listVisible && active >= 0 ? `${listId}-${active}` : undefined
            }
            aria-describedby={noticeId}
            aria-busy={currentSearch.status === "loading"}
            onChange={(event) => editSearch(event.target.value)}
            onPaste={pasteSearch}
            onFocus={() => {
              focused.current = true;
              popupAllowed.current = true;
              if (suggestions.length) setOpen(true);
            }}
            onBlur={() => {
              focused.current = false;
              popupAllowed.current = false;
              setOpen(false);
              setActive(-1);
            }}
            onKeyDown={keyDown}
          />
          {currentSearch.status === "loading" && enabled && (
            <LoaderCircle
              className="address-autocomplete__spinner"
              size={18}
              aria-hidden="true"
            />
          )}
        </div>
        {listVisible && (
          <ul
            id={listId}
            className="address-autocomplete__options"
            role="listbox"
            aria-label="US address suggestions"
          >
            {suggestions.map((suggestion, index) => (
              <li
                id={`${listId}-${index}`}
                key={suggestion.id}
                role="option"
                aria-selected={active === index}
                className={active === index ? "is-active" : ""}
                onPointerDown={(event) => event.preventDefault()}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => selectSuggestion(suggestion)}
              >
                <MapPin size={16} aria-hidden="true" />
                <span>
                  {suggestion.label}
                  {suggestion.entries > 1 && (
                    <small>
                      Choose an apartment or suite · {suggestion.entries}{" "}
                      addresses
                    </small>
                  )}
                </span>
                <ChevronRight size={16} aria-hidden="true" />
              </li>
            ))}
          </ul>
        )}
      </div>
      <p
        id={noticeId}
        className="address-autocomplete__notice"
        role="status"
        aria-live="polite"
      >
        {notice}
      </p>
      {!disabled && (
        <button
          type="button"
          className="address-autocomplete__manual-link"
          onClick={manualEntry}
        >
          Enter address manually
          <ChevronRight size={16} aria-hidden="true" />
        </button>
      )}
      <div
        id={`${scope}-address-manual`}
        className="address-autocomplete__manual"
        tabIndex={-1}
      >
        {children}
      </div>
      <div className="address-autocomplete__verification" aria-live="polite">
        {currentVerification?.status === "loading" && (
          <p>
            <LoaderCircle
              className="address-autocomplete__spinner"
              size={16}
              aria-hidden="true"
            />
            Checking this address…
          </p>
        )}
        {verified && (
          <p>
            Verified by {result?.provider} for the address shown, including the
            apartment or suite. This is address verification, not taxpayer
            identity verification.
          </p>
        )}
        {currentVerification?.status === "error" && (
          <p>
            {currentVerification.message
              ? `${sectionLabel} — Address: ${currentVerification.message} Your address remains unverified.`
              : "Address verification is temporarily unavailable. Your entered address is unchanged and is not verified."}
          </p>
        )}
        {result && !verified && !correction && (
          <p>
            This address could not be verified. Review the fields below or keep
            your manual entry.
          </p>
        )}
        {displayedIssues.length > 0 && (
          <div
            className="address-verification-summary"
            role="region"
            aria-label="Address fields to review"
          >
            <h5>
              Address needs attention{" "}
              <span>
                {new Set(displayedIssues.map((issue) => issue.field)).size}{" "}
                {new Set(displayedIssues.map((issue) => issue.field)).size === 1
                  ? "field"
                  : "fields"}
              </span>
            </h5>
            <ul className="address-autocomplete__issues">
              {displayedIssues.map((issue, index) => (
                <li key={`${issue.field}-${index}`}>
                  <strong>
                    {sectionLabel} — {fieldLabels[issue.field]}:
                  </strong>{" "}
                  {issue.reason}
                  {fieldTargets?.[issue.field] && (
                    <button
                      type="button"
                      onClick={() =>
                        document
                          .getElementById(fieldTargets[issue.field]!)
                          ?.focus()
                      }
                    >
                      Review {fieldLabels[issue.field].toLowerCase()}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        {correction && (
          <div className="address-autocomplete__correction">
            <strong>Review a suggested correction</strong>
            <p>{displayAddress(correction)}</p>
            <p>
              Your entered address has not changed. Accept this suggestion only
              if it is correct.
            </p>
            <div>
              <button
                type="button"
                className="address-autocomplete__accept"
                disabled={disabled}
                onClick={() => {
                  invalidateVerification();
                  onChange({ ...correction });
                  setVerifyAgain((count) => count + 1);
                }}
              >
                Use suggested address
              </button>
              <button
                type="button"
                className="address-autocomplete__keep"
                disabled={disabled}
                onClick={() => setKeptCorrection(valueKey)}
              >
                Keep my entered address
              </button>
            </div>
            {keptCorrection === valueKey && (
              <p>Keeping your entered address. It remains unverified.</p>
            )}
          </div>
        )}
        {enabled &&
          canVerifyAddress(value) &&
          !syntaxIssues.length &&
          !verified &&
          currentVerification?.status !== "loading" && (
            <button
              type="button"
              className="address-autocomplete__check"
              onClick={() => {
                invalidateVerification();
                setVerifyAgain((count) => count + 1);
              }}
            >
              Check address
            </button>
          )}
        {enabled && !canVerifyAddress(value) && (
          <p>
            Verification starts after a US country, street, city, state, and ZIP
            code have been provided. Add the apartment or suite when applicable.
          </p>
        )}
      </div>
    </section>
  );
}
